import React, { useMemo } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import EmptyState from "./EmptyState";
import ErrorState from "./ErrorState";
import LoadingState from "./LoadingState";
import SimpleBarChart from "./SimpleBarChart";

const buildItems = (map) => {
  if (!map || typeof map !== "object") return [];
  return Object.entries(map).map(([label, value]) => ({
    label,
    value: Number(value || 0),
  }));
};

const MetricCard = ({ title, value, helper }) => (
  <Card variant="outline" _hover={{ boxShadow: "md" }} transition="box-shadow 0.2s ease">
    <CardBody>
      <Text fontSize="sm" color="gray.500">
        {title}
      </Text>
      <Text fontSize="2xl" fontWeight="semibold">
        {value}
      </Text>
      {helper ? (
        <Text fontSize="sm" color="gray.500">
          {helper}
        </Text>
      ) : null}
    </CardBody>
  </Card>
);

const AnalyticsChartCard = ({
  title,
  description,
  series,
  items,
  colorScheme = "blue",
  formatValue,
}) => {
  const hasItems = items.length > 0;
  return (
    <Card variant="outline" _hover={{ boxShadow: "md" }} transition="box-shadow 0.2s ease">
      <CardHeader>
        <Heading size="sm">{title}</Heading>
      </CardHeader>
      <CardBody>
        {description ? (
          <Text fontSize="sm" color="gray.600">
            {description}
          </Text>
        ) : null}
        <Box mt={3}>
          <SimpleBarChart values={series} colorScheme={colorScheme} />
        </Box>
        {hasItems ? (
          <Wrap mt={3} spacing={2}>
            {items.map((item) => (
              <WrapItem key={item.label}>
                <Badge colorScheme="gray">
                  {item.label}: {formatValue ? formatValue(item.value) : item.value}
                </Badge>
              </WrapItem>
            ))}
          </Wrap>
        ) : null}
      </CardBody>
    </Card>
  );
};

const AnalyticsOverviewPanel = ({
  data,
  isLoading,
  error,
  onRetry,
  filters,
  onFilterChange,
  onApply,
  formatCurrency,
  formatDateTime,
  statusColor,
}) => {
  const summary = useMemo(() => {
    if (!data) return null;
    const payments = data.payments || {};
    const invoices = data.invoices || {};
    const refunds = data.refunds || {};
    const subscriptionInvoices = data.subscriptionInvoices || {};
    const subscriptions = data.subscriptions || {};
    const plans = data.plans || {};

    return {
      payments,
      invoices,
      refunds,
      subscriptionInvoices,
      subscriptions,
      plans,
      topCustomers: Array.isArray(data.topCustomers) ? data.topCustomers : [],
      topInvoices: Array.isArray(data.topInvoices) ? data.topInvoices : [],
      filters: data.filters || {},
      generatedAt: data.generatedAt,
      revenueTimeSeries: Array.isArray(data.revenueTimeSeries) ? data.revenueTimeSeries : [],
    };
  }, [data]);

  const filterCard = (
    <Card variant="outline" _hover={{ boxShadow: "md" }} transition="box-shadow 0.2s ease">
      <CardHeader>
        <Heading size="sm">Analytics filters</Heading>
      </CardHeader>
      <CardBody>
        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
          <FormControl>
            <FormLabel fontSize="sm" color="gray.600">
              Start date
            </FormLabel>
            <Input
              size="sm"
              type="datetime-local"
              step="0.001"
              value={filters?.startDate || ""}
              onChange={(event) => onFilterChange("startDate", event.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" color="gray.600">
              End date
            </FormLabel>
            <Input
              size="sm"
              type="datetime-local"
              step="0.001"
              value={filters?.endDate || ""}
              onChange={(event) => onFilterChange("endDate", event.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" color="gray.600">
              Include time series
            </FormLabel>
            <Select
              size="sm"
              value={filters?.includeTimeSeries ? "true" : "false"}
              onChange={(event) => onFilterChange("includeTimeSeries", event.target.value === "true")}
            >
              <option value="true">True</option>
              <option value="false">False</option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" color="gray.600">
              Time series granularity
            </FormLabel>
            <Select
              size="sm"
              value={filters?.timeSeriesGranularity || "DAY"}
              onChange={(event) => onFilterChange("timeSeriesGranularity", event.target.value)}
              isDisabled={!filters?.includeTimeSeries}
            >
              <option value="HOUR">HOUR</option>
              <option value="DAY">DAY</option>
              <option value="MONTH">MONTH</option>
            </Select>
          </FormControl>
        </SimpleGrid>
        <Flex justify="flex-end" mt={4}>
          <Button size="sm" colorScheme="blue" onClick={onApply} isLoading={isLoading}>
            Apply filters
          </Button>
        </Flex>
      </CardBody>
    </Card>
  );

  if (error) {
    return (
      <Stack spacing={6}>
        {filterCard}
        <ErrorState title="Analytics unavailable" description={error} onRetry={onRetry} />
      </Stack>
    );
  }

  if (isLoading) {
    return (
      <Stack spacing={6}>
        {filterCard}
        <LoadingState />
      </Stack>
    );
  }

  if (!summary) {
    return (
      <Stack spacing={6}>
        {filterCard}
        <EmptyState title="No analytics data" description="Analytics results will appear here." />
      </Stack>
    );
  }

  const paymentsStatusItems = buildItems(summary.payments.countByStatus);
  const paymentsMethodItems = buildItems(summary.payments.countByMethod);
  const paymentsCurrencyItems = buildItems(summary.payments.countByCurrency);
  const invoicesStatusItems = buildItems(summary.invoices.countByStatus);
  const refundsStatusItems = buildItems(summary.refunds.countByStatus);
  const subscriptionsStatusItems = buildItems(summary.subscriptions.countByStatus);
  const plansIntervalItems = buildItems(summary.plans.countByInterval);

  const revenueSeries = summary.revenueTimeSeries.map((point) => Number(point.amount || 0));

  const filterEntries = Object.entries(summary.filters || {}).filter(([, value]) => value);

  return (
    <Stack spacing={6}>
      {filterCard}
      <Flex justify="space-between" align="flex-start" flexWrap="wrap" gap={3}>
        <Box>
          <Heading size="sm">Analytics overview</Heading>
          <Text fontSize="sm" color="gray.500">
            Generated {formatDateTime(summary.generatedAt)}.
          </Text>
        </Box>
        <Box>
          <Text fontSize="xs" textTransform="uppercase" color="gray.500" mb={1}>
            Filters
          </Text>
          {filterEntries.length === 0 ? (
            <Text fontSize="sm" color="gray.500">
              No filters applied.
            </Text>
          ) : (
            <Wrap spacing={2}>
              {filterEntries.map(([key, value]) => (
                <WrapItem key={key}>
                  <Badge colorScheme="blue">
                    {key}: {String(value)}
                  </Badge>
                </WrapItem>
              ))}
            </Wrap>
          )}
        </Box>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
        <MetricCard
          title="Payments total"
          value={formatCurrency(summary.payments.totalAmount)}
          helper={`${summary.payments.totalCount || 0} payments`}
        />
        <MetricCard
          title="Net amount"
          value={formatCurrency(summary.payments.netAmount)}
          helper={`Avg ${formatCurrency(summary.payments.averageAmount)}`}
        />
        <MetricCard
          title="Invoices total"
          value={formatCurrency(summary.invoices.totalAmount)}
          helper={`${summary.invoices.totalCount || 0} invoices`}
        />
        <MetricCard
          title="Outstanding invoices"
          value={formatCurrency(summary.invoices.outstandingAmount)}
          helper={`Paid ${formatCurrency(summary.invoices.paidAmount)}`}
        />
        <MetricCard
          title="Refunds total"
          value={formatCurrency(summary.refunds.totalAmount)}
          helper={`${summary.refunds.totalCount || 0} refunds`}
        />
        <MetricCard
          title="Subscription invoices paid"
          value={formatCurrency(summary.subscriptionInvoices.totalAmountPaid)}
          helper={`Due ${formatCurrency(summary.subscriptionInvoices.totalAmountDue)}`}
        />
        <MetricCard
          title="Active plans"
          value={`${summary.plans.activeCount || 0}`}
          helper={`Inactive ${summary.plans.inactiveCount || 0}`}
        />
        <MetricCard
          title="Subscriptions"
          value={`${summary.subscriptions.totalCount || 0}`}
          helper="Total subscriptions"
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <AnalyticsChartCard
          title="Revenue time series"
          description="Total revenue for the selected range."
          series={revenueSeries}
          items={[]}
          colorScheme="blue"
          formatValue={formatCurrency}
        />
        <AnalyticsChartCard
          title="Payments by status"
          description="Count of payments by status."
          series={paymentsStatusItems.map((item) => item.value)}
          items={paymentsStatusItems}
          colorScheme="green"
        />
        <AnalyticsChartCard
          title="Invoices by status"
          description="Count of invoices by status."
          series={invoicesStatusItems.map((item) => item.value)}
          items={invoicesStatusItems}
          colorScheme="purple"
        />
        <AnalyticsChartCard
          title="Refunds by status"
          description="Refund volume by status."
          series={refundsStatusItems.map((item) => item.value)}
          items={refundsStatusItems}
          colorScheme="orange"
        />
        <AnalyticsChartCard
          title="Subscriptions by status"
          description="Subscriptions split by status."
          series={subscriptionsStatusItems.map((item) => item.value)}
          items={subscriptionsStatusItems}
          colorScheme="teal"
        />
        <AnalyticsChartCard
          title="Plans by interval"
          description="Plans grouped by billing interval."
          series={plansIntervalItems.map((item) => item.value)}
          items={plansIntervalItems}
          colorScheme="cyan"
        />
        <AnalyticsChartCard
          title="Payments by method"
          description="Payment method distribution."
          series={paymentsMethodItems.map((item) => item.value)}
          items={paymentsMethodItems}
          colorScheme="pink"
        />
        <AnalyticsChartCard
          title="Payments by currency"
          description="Payments grouped by currency."
          series={paymentsCurrencyItems.map((item) => item.value)}
          items={paymentsCurrencyItems}
          colorScheme="gray"
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <Card variant="outline" _hover={{ boxShadow: "md" }} transition="box-shadow 0.2s ease">
          <CardHeader>
            <Heading size="sm">Top customers</Heading>
          </CardHeader>
          <CardBody>
            {summary.topCustomers.length === 0 ? (
              <EmptyState title="No top customers" description="Top customer data will appear here." />
            ) : (
              <TableContainer>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Email</Th>
                      <Th isNumeric>Total amount</Th>
                      <Th isNumeric>Payments</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {summary.topCustomers.map((customer) => (
                      <Tr key={customer.email}>
                        <Td>{customer.email}</Td>
                        <Td isNumeric>{formatCurrency(customer.totalAmount)}</Td>
                        <Td isNumeric>{customer.paymentCount}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </CardBody>
        </Card>
        <Card variant="outline" _hover={{ boxShadow: "md" }} transition="box-shadow 0.2s ease">
          <CardHeader>
            <Heading size="sm">Top invoices</Heading>
          </CardHeader>
          <CardBody>
            {summary.topInvoices.length === 0 ? (
              <EmptyState title="No top invoices" description="Top invoice data will appear here." />
            ) : (
              <TableContainer>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Invoice</Th>
                      <Th>Customer</Th>
                      <Th isNumeric>Amount</Th>
                      <Th>Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {summary.topInvoices.map((invoice) => (
                      <Tr key={`${invoice.invoiceId || invoice.invoiceNumber}-${invoice.customerEmail}`}>
                        <Td>{invoice.invoiceNumber || `#${invoice.invoiceId}`}</Td>
                        <Td>{invoice.customerEmail}</Td>
                        <Td isNumeric>{formatCurrency(invoice.amount)}</Td>
                        <Td>
                          <Badge colorScheme={statusColor(invoice.status)}>{invoice.status}</Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </CardBody>
        </Card>
      </SimpleGrid>
    </Stack>
  );
};

export default AnalyticsOverviewPanel;
